import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { EdpModalService } from "@edp-components/edp-modal";
import { EdpEsContractListComponent } from "@edp-components/ES/edp-es-contract-list/edp-es-contract-list.component";
import { RegionEnum } from "@edp-enums/region.enum";
import { ContractEs, ContractEsFactory } from "@edp-models/ES/es-contract.model";
import { ContractService } from "@edp-services/contract.service";
import { ContractEsService } from "@edp-services/ES/contract-es.service";
import { GoogleAnalyticsService } from "@edp-services/google-analytics.service";
import { RegionService } from "@edp-services/region.service";
import { SetCampaigns } from "@edp-store/actions/dashboard.actions";
import {
    EContractEsActions,
    GetContractsEs,
    IsSingleContractEs,
    SelectContractEs,
    SetContractsEs,
    ShowContractSelectionEs,
} from "@edp-store/actions/ES/contract-es.actions";
import { EDocumentEsActions } from "@edp-store/actions/ES/document-es.actions";
import { EEsProductActions } from "@edp-store/actions/ES/product-es.actions";
import { ELayoutActions, RefreshMainMenu, SetHeaderAlert, ShowLoader } from "@edp-store/actions/layout.actions";
import { getRouterState } from "@edp-store/selectors/router.selectors";
import { IAppState } from "@edp-store/state/app.state";
import { IAppEsState } from "@edp-store/state/ES/app-es.state";
import { RouterStateUrl } from "@edp-utils/route-serializer";
import { Actions, Effect, ofType } from "@ngrx/effects";
import { RouterReducerState } from "@ngrx/router-store";
import { select, Store } from "@ngrx/store";
import { EMPTY, of } from "rxjs";
import { catchError, map, mergeMap, switchMap, take, tap, withLatestFrom } from "rxjs/operators";

@Injectable()
export class ContractEsEffects {
    @Effect()
    loadContracts$ = this.actions$.pipe(
        ofType(EContractEsActions.GetContractsEs),
        tap(() => this.store.dispatch(new ShowLoader(true))),
        switchMap((action: GetContractsEs) =>
            this.contractEsService.getAll().pipe(catchError((error: Error) => of(new ShowLoader(false))))
        ),
        withLatestFrom(this.store.select(getRouterState)),
        mergeMap(([contracts, router]: [ContractEs[] | Error, RouterReducerState<RouterStateUrl>]) => {
            if (this.regionService.getRegion() !== RegionEnum.ES) {
                return of(new ShowLoader(false));
            }
            if (contracts instanceof Error) {
                // TO DO API ERROR
                return;
            }
            const contractList: ContractEs[] = contracts.map(contract => ContractEsFactory.create(contract));

            let contract: ContractEs;

            if (router.state.params.contract_id) {
                contract = contractList.find(contract => contract.id === router.state.params.contract_id);
            }

            if (!contract) {
                const activeContracts = contractList.filter(contract => contract.isActived());

                if (activeContracts.length === 1 || contractList.length === 1) {
                    contract = contractList.length === 1 ? contractList[0] : activeContracts[0];
                }
            }

            /* if (contract) {
                this.store.dispatch(new SelectContractEs(contract));
            } */

            if (contractList.length > 1) {
                this.store.dispatch(new IsSingleContractEs(false));
            }

            if (!contract && !router.state.data.skipContractSelection) {
                this.store.dispatch(new ShowContractSelectionEs());
            }

            return of(new SetContractsEs(contractList), new ShowLoader(false));
        })
    );

    @Effect()
    selectContract$ = this.actions$.pipe(
        ofType(EContractEsActions.SelectContractEs),
        // switchMap((action: SelectContract) => {
        switchMap((action: SelectContractEs) => {
            if (this.regionService.getRegion() !== RegionEnum.ES) {
                return of(new ShowLoader(false));
            }
            this.googleAnalyticsService.pageChangeEvent();
            this.store.dispatch(new RefreshMainMenu());
            this.store.dispatch(new SetCampaigns(null));
            const actions = [
                {
                    type: EDocumentEsActions.GetDocumentEs,
                    contractId: action.contract.id,
                },
                {
                    type: EEsProductActions.GetAdditionalProductEs,
                    contractId: action.contract.id,
                },
            ];

            return [...actions];
        })
    );

    @Effect({ dispatch: false })
    showContractSelection$ = this.actions$.pipe(
        ofType(EContractEsActions.ShowContractSelectionEs),
        tap(() => {
            if (this.regionService.getRegion() !== RegionEnum.ES) {
                return of(new ShowLoader(false));
            }
            const { options, modal } = this.contractService.getFullModalParam(false);
            this.edpModalService.custom(EdpEsContractListComponent, options, modal).then(() => {
                /* Intentionally silent */
            });
        })
    );

    @Effect({ dispatch: false })
    hideContractSelection$ = this.actions$.pipe(
        ofType(EContractEsActions.HideContractSelectionEs),
        tap(() => {
            if (this.edpModalService.hasInstance().length) {
                this.edpModalService.removeAll();
            }
        })
    );

    constructor(
        private actions$: Actions,
        private contractService: ContractService,
        private contractEsService: ContractEsService,
        private store: Store<IAppState>,
        private storeEs: Store<IAppEsState>,
        private edpModalService: EdpModalService,
        private googleAnalyticsService: GoogleAnalyticsService,
        private router: Router,
        private regionService: RegionService
    ) {}
}
